import { Grid, Button, makeStyles } from '@material-ui/core'
import type { FullMetaFragment } from '@paintscout/api'
import { getObjectLabels } from '@paintscout/util/builder'
import classNames from 'classnames'
import React, { useState, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import ActionButton from '../ActionButton'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import AssignmentIcon from '@material-ui/icons/Assignment'
import SettingsIcon from '@material-ui/icons/Settings'
import PeopleIcon from '@material-ui/icons/People'
import { GoogleMap, Marker, InfoWindowF } from '@react-google-maps/api'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import CloseIcon from '@material-ui/icons/Close'
import LaunchRoundedIcon from '@material-ui/icons/LaunchRounded'
import { useDialogs } from '../../providers/DialogStack'
import Divider from '../Divider'
import Typography from '../Typography'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import TransferToLocationDialog from '../../dialogs/TransferToLocationDialog'
import { useGoogleMaps } from '../../providers/GoogleMapsProvider'

const useStyles = makeStyles<Theme, GoogleMapsComponentProps>(
  (theme) => ({
    root: {
      position: 'relative'
    },
    totalsContainer: {
      position: 'absolute',
      top: theme.spacing(),
      left: theme.spacing(),
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    },
    totalsDiv: {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.borderRadius.lg,
      boxShadow: theme.shadows[3],
      margin: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      position: 'relative',
      width: '180px',
      height: '210px'
    },
    totalHeader: {
      maxWidth: '80%'
    },
    subtitleText: {
      fontWeight: '600 !important'
    },
    totalValue: {
      paddingBottom: 0,
      fontWeight: 500,
      fontSize: '4rem',
      lineHeight: '1em'
    },
    totalsLinkIcon: {
      fontSize: '1.5rem',
      color: theme.paintscout.palette.brand.Neutral.P60,
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
      cursor: 'pointer'
    },
    infoIconHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(0.5)
    },
    closeIcon: {
      marginLeft: theme.spacing()
    },
    infoIcon: {
      padding: theme.spacing(0.5),
      minWidth: '300px',
      overflow: 'hidden',
      zIndex: 1000
    },
    viewButton: {
      marginTop: theme.spacing()
    },
    hover: {
      '&:hover': {
        cursor: 'pointer',
        color: theme.palette.primary.main
      }
    }
  }),
  { name: 'GoogleMapsComponent' }
)

export interface GoogleMapsComponentProps {
  classes?: StyleClasses<typeof useStyles>
  locations: FullMetaFragment[]
  showTotals?: boolean
}

function GoogleMapsComponent(props: GoogleMapsComponentProps) {
  const classes = useStyles(props)
  const { locations, showTotals } = props
  const history = useHistory()
  const { openDialog, dismissDialog } = useDialogs()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const [totalLocations, setTotalLocations] = useState(locations.length)
  const [totalUsers, setTotalUsers] = useState(locations.reduce((acc, row) => acc + (row?.userCounts?.all ?? 0), 0))
  const [display, setDisplay] = useState({})
  const [center, setCenter] = useState(
    calculateCenterPoint(
      locations.map((location) => {
        return {
          lat: location.options?.companyCoordinates[1],
          lng: location.options?.companyCoordinates[0]
        }
      })
    )
  )
  const toggleDisplay = (id: string) => {
    const location = locations.find((location) => location._id === id)
    const newCenter = {
      lat: location.options?.companyCoordinates[1],
      lng: location.options?.companyCoordinates[0]
    }
    setCenter(newCenter)
    setDisplay({ ...display, [id]: !display[id] })
  }
  useEffect(() => {
    setTotalLocations(locations.length)
    setTotalUsers(locations.reduce((acc, location) => acc + (location?.userCounts?.all ?? 0), 0))
    setCenter(
      calculateCenterPoint(
        locations.map((location) => {
          return {
            lat: location.options?.companyCoordinates[1],
            lng: location.options?.companyCoordinates[0]
          }
        })
      )
    )
  }, [locations])

  const rowActions = [
    {
      key: 'quotes',
      label: `${objectLabels.quote.plural}`,
      icon: AssignmentIcon
    },
    {
      key: 'settings',
      label: 'Settings',
      icon: SettingsIcon
    },
    {
      key: 'users',
      label: 'Users',
      icon: PeopleIcon
    },
    {
      key: 'transfer',
      label: 'Transfer',
      icon: SwapHorizIcon
    }
  ]

  const handleAction = useCallback((action: string, rows: any[]) => {
    let url = ''
    if (action === 'transfer') {
      openDialog(TransferToLocationDialog, {
        locationName: rows[0].name,
        locationId: rows[0]._id,
        onConfirm: dismissDialog,
        onCancel: dismissDialog
      })
    } else {
      switch (action) {
        case 'quotes':
          url = `/quotes`
          break
        case 'settings':
          url = `/settings`
          break
        case 'users':
          url = `/settings?section=billing-users`
          break
        default:
          url = '/dashboard'
          break
      }

      window.localStorage.setItem('masqueradeCompanyId', rows[0]._id)
      history.push(url)
    }
  }, [])

  const containerStyle = {
    width: '100%',
    height: '100vh'
  }

  const { isLoaded } = useGoogleMaps()

  const onClick = useCallback((location) => {
    window.localStorage.setItem('masqueradeCompanyId', location._id)
    history.push('/dashboard')
  }, [])

  // Alternate icon for business, but need to work on its offset as its top left corner is on the coordinates, so looks bad
  const _businessIcon = {
    path: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z',
    fillColor: '#1f1f1f',
    fillOpacity: 0.9,
    scale: 1,
    strokeColor: '#1f1f1f',
    strokeWeight: 0.25
  }

  return (
    <div className={classes.root}>
      {isLoaded && center ? (
        <>
          {showTotals && (
            <div className={classes.totalsContainer}>
              <div className={classes.totalsDiv}>
                <LaunchRoundedIcon className={classes.totalsLinkIcon} onClick={() => history.push('/locations')} />
                <Typography className={classes.totalHeader} variant="h2">
                  Total Locations
                </Typography>
                <Typography className={classes.totalValue} variant="h1">
                  {totalLocations}
                </Typography>
              </div>
              <div className={classes.totalsDiv}>
                <Typography className={classes.totalHeader} variant="h2">
                  Total Users
                </Typography>
                <Typography className={classes.totalValue} variant="h1">
                  {totalUsers}
                </Typography>
              </div>
            </div>
          )}
          <GoogleMap
            options={{ disableDefaultUI: showTotals }}
            mapContainerStyle={containerStyle}
            center={center}
            zoom={5}
          >
            {locations.map((location, i) => {
              return (
                <>
                  <Marker
                    key={i}
                    // icon={businessIcon}
                    title={location.name}
                    position={{
                      lng: location.options?.companyCoordinates[0],
                      lat: location.options?.companyCoordinates[1]
                    }}
                    onClick={() => toggleDisplay(location._id)}
                  />
                  {display[location._id] && (
                    <InfoWindowF
                      position={{
                        lng: location.options?.companyCoordinates[0],
                        lat: location.options?.companyCoordinates[1]
                      }}
                      options={{
                        headerDisabled: true
                      }}
                    >
                      <div className={classes.infoIcon}>
                        <div className={classes.infoIconHeader}>
                          <Typography variant="h6">{location.name}</Typography>
                          <CloseIcon
                            className={classNames(classes.closeIcon, classes.hover)}
                            onClick={() => toggleDisplay(location._id)}
                          />
                        </div>

                        <Typography variant="subtitle2">{location.address}</Typography>
                        <Typography variant="subtitle2" className={classes.subtitleText}>
                          {location.city}, {location.province}
                        </Typography>
                        <Divider greyValue={'A200'} classes={{ marginBottom: '5px' }} />
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="h6">Users:</Typography>
                            <Typography variant="subtitle1">{location?.userCounts?.all ?? '0'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="h6">Quotes:</Typography>
                            <Typography variant="subtitle1">{location?.aggregates?.totalQuotes ?? '0'}</Typography>
                          </Grid>
                        </Grid>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="h6">Sales:</Typography>
                            <Typography variant="subtitle1">
                              ${location?.aggregates?.closedValue?.toFixed(2) ?? 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="h6">Win Rate:</Typography>
                            <Typography variant="subtitle1">
                              {location?.aggregates?.closeRate?.toFixed(2) ?? 0}%
                            </Typography>
                          </Grid>
                        </Grid>
                        <Divider greyValue={'A200'} />
                        <Grid container spacing={2}>
                          <Grid item xs={10}>
                            <Button
                              variant="contained"
                              color="primary"
                              className={classes.viewButton}
                              onClick={() => onClick(location)}
                            >
                              View Location
                            </Button>
                          </Grid>
                          <Grid item xs={1} alignItems="flex-end">
                            <ActionButton
                              data-testid="actions"
                              actions={rowActions}
                              onActionClick={(ev, action) => handleAction(action, [location])}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    </InfoWindowF>
                  )}
                </>
              )
            })}
          </GoogleMap>
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

interface Coordinate {
  lat: number
  lng: number
}

export function calculateCenterPoint(coordinates: Coordinate[]): Coordinate | null {
  if (coordinates.length === 0) {
    return null
  }

  let sumLat: number = 0
  let sumLng: number = 0

  for (const coord of coordinates) {
    sumLat += coord.lat
    sumLng += coord.lng
  }

  const centerLat: number = sumLat / coordinates.length
  const centerLng: number = sumLng / coordinates.length

  return { lat: centerLat, lng: centerLng }
}

export default GoogleMapsComponent
